import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { OnlineProductOrder } from './online-product-order.js';

export class ProtocolProductOrderCreatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = 'c5f7471d926a4321969bce395cbbcdb6';

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly order: OnlineProductOrder,
  ) {
    super();
  }

  get $typeId(): string {
    return ProtocolProductOrderCreatedEvent.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): ProtocolProductOrderCreatedEvent {
    return new ProtocolProductOrderCreatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        context.fromData<OnlineProductOrder>(MapUtils.get<SerializedData>(map, 'order')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'orderId': this.order.uniqueId,
      'order': context.toData(this.order),
    };
  }

  clone(): this {
    return new ProtocolProductOrderCreatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.order.clone(),
    ) as this;
  }

  isSame(other: ProtocolProductOrderCreatedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        isSameData(other.order, this.order);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof ProtocolProductOrderCreatedEvent;
  }

  static assertType(order: DataModel): order is ProtocolProductOrderCreatedEvent {
    return order instanceof ProtocolProductOrderCreatedEvent;
  }
}

export class ProtocolProductOrderCreatedEventBuilder extends DataModelBuilder<ProtocolProductOrderCreatedEvent> {
  build(context: SerializationContext, map: SerializedData): ProtocolProductOrderCreatedEvent {
    return ProtocolProductOrderCreatedEvent.fromMap(context, map);
  }
}
